import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {RefreshCcw, DownloadIcon} from 'lucide-react'
import Plot from 'react-plotly.js';

const StatsPlot = ({ redThreshold, greenThreshold, API_BASE, onPointClick, isDarkMode, selectedSlidePath }) => {
    const [statsData, setStatsData] = useState({ stats: [] });
    const [loading, setLoading] = useState(false);
    const [isDataFromCache, setIsDataFromCache] = useState(false);
    const [highlightedPointPath, setHighlightedPointPath] = useState(null);
    
    // State for antibodies list and the currently selected antibody
    const [antibodies, setAntibodies] = useState([]);
    const [selectedAntibody, setSelectedAntibody] = useState('All');

    // Fetch the list of distinct antibodies when the component mounts
    useEffect(() => {
        const fetchAntibodies = async () => {
            try {
                const response = await fetch(`${API_BASE}/antibodies`);
                const data = await response.json();
                if (response.ok) {
                    setAntibodies(['All', ...data]);
                } else {
                    console.error("Failed to fetch antibodies:", data.error);
                }
            } catch (error) {
                console.error("Error fetching antibodies:", error);
            }
        };
        fetchAntibodies();
    }, [API_BASE]);

    // Effect to handle highlighting points when a slide is selected from the parent
    useEffect(() => {
        setHighlightedPointPath(selectedSlidePath);
    }, [selectedSlidePath]);

    // Callback to fetch statistics data from the API
    const fetchStats = useCallback(async (forceRefresh = false) => {
        setLoading(true);
        try {
            let url = `${API_BASE}/stats?red_threshold=${redThreshold}&green_threshold=${greenThreshold}`;
            if (forceRefresh) {
                url += '&force_refresh=true';
            }
            const response = await fetch(url);
            const data = await response.json();
            if (response.ok) {
                setStatsData(data);
                setIsDataFromCache(data.from_cache || false);
                console.log("Fetched all stats:", data);
            } else {
                console.error("Failed to fetch stats:", data.error);
                setStatsData({ stats: [] });
                setIsDataFromCache(false);
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
            setIsDataFromCache(false);
        } finally {
            setLoading(false);
        }
    }, [API_BASE, redThreshold, greenThreshold]);

    // Effect to trigger fetching stats with a debounce
    useEffect(() => {
        const handler = setTimeout(() => {
            fetchStats(false);
        }, 500);

        return () => clearTimeout(handler);
    }, [fetchStats]);

    // Handler to force a recalculation, bypassing the cache
    const handleRecalculate = () => {
        fetchStats(true);
    };

    // NEW: Handler to download all stats as a CSV file
    const handleDownloadCSV = useCallback(() => {
        const dataToDownload = statsData.stats;

        if (!dataToDownload || dataToDownload.length === 0) {
            alert("No data available to download.");
            return;
        }

        // Define the headers for the CSV file
        const headers = ['path', 'antibody', 'treatment', 'mouse', 'green_positive_ratio', 'red_positive_ratio', 'both_positive_ratio'];
        
        // Convert the array of objects to a CSV string
        const csvContent = [
            headers.join(','), // First row is the headers
            ...dataToDownload.map(row => 
                headers.map(fieldName => {
                    const value = row[fieldName];
                    return (value === null || value === undefined) ? '' : value;
                }).join(',')
            )
        ].join('\n');

        // Create a Blob and trigger the download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "stats_data.csv");
        document.body.appendChild(link); // Required for Firefox
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

    }, [statsData.stats]); // Depends on the full stats data

    // Memoized client-side filtering of the stats data
    const filteredStats = useMemo(() => {
        if (!statsData.stats || selectedAntibody === 'All') {
            return statsData.stats;
        }
        return statsData.stats.filter(p => p.antibody === selectedAntibody);
    }, [statsData, selectedAntibody]);

    // Memoized calculation for Plotly data
    const plotlyData = useMemo(() => {
        if (!filteredStats) {
            return [];
        }

        const dataMap = {
            cy3_Veh: [], cy3_ART0380: [], cy3_Vec: [],
            cy5_Veh: [], cy5_ART0380: [], cy5_Vec: [],
            cy35_Veh: [], cy35_ART0380: [], cy35_Vec: []
        };
        
        filteredStats.forEach(p => {
            const transformedPath = p.path;
            if (p.green_positive_ratio !== null) {
                dataMap[`cy3_${p.treatment}`].push({ ratio: parseFloat(p.green_positive_ratio), path: transformedPath, mouse: p.mouse });
            }
            if (p.red_positive_ratio !== null) {
                dataMap[`cy5_${p.treatment}`].push({ ratio: parseFloat(p.red_positive_ratio), path: transformedPath, mouse: p.mouse });
            }
            if (p.both_positive_ratio !== null) {
                dataMap[`cy35_${p.treatment}`].push({ ratio: parseFloat(p.both_positive_ratio), path: transformedPath, mouse: p.mouse });
            }
        });

        const traces = [];
        const colors = { 'Veh': '#0088FE', 'ART0380': '#FF8042', 'Vec': '#00C49F' };
        const conditions = [
            { key: 'cy3_Veh', xLabel: 'cy3 (green)', treatment: 'Veh' },
            { key: 'cy3_ART0380', xLabel: 'cy3 (green)', treatment: 'ART0380' },
            { key: 'cy3_Vec', xLabel: 'cy3 (green)', treatment: 'Vec' },
            { key: 'cy5_Veh', xLabel: 'cy5 (red)', treatment: 'Veh' },
            { key: 'cy5_ART0380', xLabel: 'cy5 (red)', treatment: 'ART0380' },
            { key: 'cy5_Vec', xLabel: 'cy5 (red)', treatment: 'Vec' },
            { key: 'cy35_Veh', xLabel: 'cy3+cy5', treatment: 'Veh' },
            { key: 'cy35_ART0380', xLabel: 'cy3+cy5', treatment: 'ART0380' },
            { key: 'cy35_Vec', xLabel: 'cy3+cy5', treatment: 'Vec' }
        ];

        conditions.forEach(cond => {
            const points = dataMap[cond.key];
            if (points.length > 0) {
                const pointSizes = points.map(p => p.path === highlightedPointPath ? 12 : 8);
                const pointBorderWidths = points.map(p => p.path === highlightedPointPath ? 2 : 0);
                const pointBorderColors = points.map(p => p.path === highlightedPointPath ? '#FFFF00' : colors[cond.treatment]);

                traces.push({
                    y: points.map(p => p.ratio),
                    x: Array(points.length).fill(cond.xLabel),
                    name: cond.treatment,
                    type: 'box',
                    boxpoints: 'all',
                    width: 0,
                    pointwidth: 0.4,
                    jitter: 0.5,
                    pointpos: 0,
                    marker: { 
                        color: colors[cond.treatment], 
                        size: pointSizes, 
                        opacity: 0.8,
                        line: { color: pointBorderColors, width: pointBorderWidths }
                    },
                    legendgroup: cond.treatment,
                    showlegend: cond.xLabel === 'cy3+cy5',
                    customdata: points.map(p => p.path),
                    hoverinfo: 'y+text',
                    text: points.map(p => "mouse: " + p.mouse),
                });
            }
        });

        return traces;
    }, [filteredStats, highlightedPointPath]);
    
    // Memoized layout object for Plotly
    const layout = useMemo(() => {
        const baseLayout = {
            boxmode: 'group',
            margin: { l: 40, r: 10, b: 40, t: 10 },
            showlegend: true,
            legend: { x: 1, xanchor: 'right', y: 1 },
            paper_bgcolor: 'rgba(0,0,0,0)',
            plot_bgcolor: 'rgba(0,0,0,0)',
        };

        const themeStyles = isDarkMode ? {
            font: { color: '#e2e8f0' },
            yaxis: { title: 'Positive Cell Ratio', zeroline: false, gridcolor: '#4a5568' },
            xaxis: { title: 'Stain & Treatment', gridcolor: '#4a5568' },
        } : {
            font: { color: '#374151' },
            yaxis: { title: 'Positive Cell Ratio', zeroline: false, gridcolor: '#d1d5db' },
            xaxis: { title: 'Stain & Treatment', gridcolor: '#d1d5db' },
        };
        return { ...baseLayout, ...themeStyles };
    }, [isDarkMode]);

    const handlePlotClick = (data) => {
        if (data.points && data.points.length > 0) {
            const point = data.points[0];
            const slidePath = point.customdata; 
            if (slidePath && onPointClick) {
                onPointClick(slidePath);
            }
        }
    };

    return (
        <div className="pt-1 w-full">
            <div className="px-4 mb-2 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <label htmlFor="antibody-select" className="text-sm font-medium text-gray-700 dark:text-gray-300">Antibody:</label>
                    <select
                        id="antibody-select"
                        value={selectedAntibody}
                        onChange={(e) => setSelectedAntibody(e.target.value)}
                        className="block w-full pl-3 pr-10 py-1 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        disabled={loading && !statsData.stats.length}
                    >
                        {antibodies.map(ab => (
                            <option key={ab} value={ab}>{ab}</option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center space-x-2">
                    {isDataFromCache && (
                        <div className="text-xs flex items-center">
                            <button 
                                onClick={handleRecalculate} 
                                className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                                disabled={loading}
                            >
                                {loading ? '...' : <RefreshCcw className="w-4 h-4" />}
                            </button>
                        </div>
                    )}
                    <button
                        onClick={handleDownloadCSV}
                        disabled={!statsData.stats || statsData.stats.length === 0 || loading}
                        className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-xs disabled:bg-gray-400 disabled:cursor-not-allowed"
                        title="Download CSV"
                    >
                        <DownloadIcon className='w-4 h-4'/>
                    </button>
                </div>
            </div>

            {loading && !statsData.stats.length && <div className="text-center text-gray-500 dark:text-gray-400 py-4">Updating plots...</div>}
            
            <div style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 300ms' }}>
                <Plot
                    data={plotlyData}
                    layout={layout}
                    style={{ width: '100%', height: '250px' }}
                    useResizeHandler={true}
                    onClick={handlePlotClick}
                    config={{ displayModeBar: false }}
                />
            </div>
        </div>
    );
}

export default StatsPlot;